import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button } from 'reactstrap';
import { useAlertPlus } from '../../../hooks';
import AlertModal from '../../../components/AlertModal';
import Form from './Form';
import config from 'config';
import { ApiService } from '../../../services';
import { getPreviewSrc, encodeFileName } from '../../../helpers/filesPreview';

import './Edit.scss';

export interface SuspensionRequest {
  id: string | undefined;
  nombre: string;
  apellido: string;
  delegacion: string;

  motivo_solicitud: string;
  fecha_desde: string;
  fecha_hasta: string;

  nro_resolucion: string | null;
  ano_resolucion: number | null;

  motivo_rechazo: string;

  state_id: string;

  attachs: RequestAttachment[];
}

export interface RequestAttachment {
  id: number;
  request_id: number;
  request_type: string;
  attachment_type: string;
  url: string;
  created_at: string;
}

export interface GalleryItem {
  src: string;
  downloadUrl: string;
  alt: string;
}

function Edit() {
  const { id } = useParams();

  const [data, setData] = useState<SuspensionRequest>({
    id: id,
    nombre: '',
    apellido: '',

    delegacion: '',

    motivo_solicitud: '',
    fecha_desde: '',
    fecha_hasta: '',

    nro_resolucion: null,
    ano_resolucion: null,

    motivo_rechazo: '',

    state_id: '',

    attachs: [],
  });

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const galleryContainer = useRef(null);

  const [resolucionFile, setResolucionFile] = useState<File | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');

  const handleSend = async (accept = false) => {
    try {
      if (accept && !resolucionFile) {
        alert('Adjunte el archivo PDF con la resolución para aceptar la solicitud.');
        return;
      }

      setIsLoading(true);

      // Preparamos los datos
      const formData = new FormData();
      formData.append('nro_resolucion', data.nro_resolucion ?? '');
      formData.append('ano_resolucion', data.ano_resolucion ? `${data.ano_resolucion}` : '');
      formData.append('motivo_rechazo', data.motivo_rechazo ?? '');
      if (accept && resolucionFile) {
        formData.append('archivo_resolucion', resolucionFile);
      }

      const headers: HeadersInit = {
        ...(localStorage.getItem('accessToken') && {
          Authorization: localStorage.getItem('accessToken') as string,
        }),
      };

      const res = await fetch(`${config.baseUrl}/matriculates/suspension/${ id }/${ accept ? 'accept' : 'reject' }`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const response = await res.json();

      if (response?.status !== 'ok') {
        console.info(response);

        if (response.payload?.message) {
          alert(response.payload.message);
          return;
        }

        alert('Ha ocurrido un error al enviar al servidor, por favor intente nuevamente.');
        return;
      }

      alert('Se guardó correctamente la resolución.', () => {
        window.location.href = '/admin/matriculates/requests';
      });
    } catch (err) {
      console.error(err);
      alert('Ha ocurrido un error al guardar la resolución, por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const ATTACHMENTS_CONFIG = [
    { key: 'documentacion_motivo', alt: 'Documentación respaldatoria' },
  ] as const;

  useEffect(() => {
    ApiService.getOne('matriculates/suspension', id)
      .then(({ payload }) => {
        setData(payload);

        const attachs = ATTACHMENTS_CONFIG.flatMap(({ key, alt }) => {
          if (!payload.attachs?.length) return [];

          const filtered = payload.attachs
            .filter((a: RequestAttachment) => a.attachment_type.startsWith(key))
            .sort((a: RequestAttachment, b: RequestAttachment) => {
              const indexA = parseInt(a.attachment_type.split('_').pop() || '0');
              const indexB = parseInt(b.attachment_type.split('_').pop() || '0');
              return indexA - indexB;
            });

          let i = 0;
          return filtered.map((a: RequestAttachment) => {
            const encodedPath = encodeFileName(a.url);
            i++;

            return {
              src: getPreviewSrc(encodedPath),
              downloadUrl: `${config.baseUrl}/${encodedPath}`,
              // @ts-ignore
              alt: `${alt} ${i}`,
            };
          });
        });

        setGallery(attachs);
      })
      .finally(() =>{
        setIsLoading(false);
      });
  }, [id]);

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <Row>
              <Col md="8">
                <h3 className="mb-0">Solicitud de Cancelación #{ id }</h3>
              </Col>
              <Col md="4" className="text-right">
                <Link to='/admin/matriculates/requests'>
                  <Button color="primary" size="md">Volver</Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form
              data={ data }
              setData={ setData }
              setResolucionFile={ setResolucionFile }
              gallery={ gallery }
              galleryContainer={ galleryContainer }
              isLoading={ isLoading }
              onAccept={ () => handleSend(true) }
              onReject={ () => handleSend(false) }
            />
          </CardBody>
        </Card>
      </Container>
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
    </>
  );
}

export default Edit;
